import { DialogRef } from '@angular/cdk/dialog';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterOutlet } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import { PopupSessionService } from 'projects/shared/src/lib/core/services/popup-session.service';
import { ChangelogUpdatesService } from 'projects/shared/src/lib/core/services/changelog-updates.service';
import { CdkDialogService } from 'projects/shared/src/lib/core/services/cdk-dialog.service';
import { POPUP_DEFAULTS } from 'projects/shared/src/lib/core/constants/global-constants';
import { NavigationService } from 'projects/shared/src/lib/core/services/navigation.service';
import { SignalRService } from 'projects/shared/src/lib/core/services/signalr.service';
import { RouterAuthEnum } from 'projects/shared/src/lib/infrastructure/enums/router-link.enum';
import { AuthStore } from 'projects/shared/src/lib/store/auth.store';
import {
  AtrActivationPopupComponent,
  AtrActivationPopupResult,
} from 'projects/heorot/src/app/components/atr-activation-popup/atr-activation-popup.component';
import {
  BulkDeletePopupComponent,
  BulkDeletePopupResult,
} from 'projects/heorot/src/app/components/bulk-delete-popup/bulk-delete-popup.component';
import { SideNavigationComponent } from 'projects/heorot/src/app/components/side-navigation/side-navigation.component';
import { ENVIRONMENT_TOKEN } from 'projects/shared/src/lib/core/injection-tokens/environment.token';
import { ComponentType } from '@angular/cdk/portal';

@Component({
  selector: 'app-root',
  imports: [FormsModule, RouterOutlet, SideNavigationComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private get isAuthenticating() {
    return window.location.pathname.startsWith(`/${RouterAuthEnum.Callback}`);
  }

  // Temporarily block that logout functionality to avoid of potential problems since the browser event order does not garantee the correct behavior
  // @HostListener('window:beforeunload')
  // beforeUnloadHandler() {
  //   if (this.authStore.authInfo().isAuthenticated) {
  //     sessionStorage.setItem('isUnloading', 'true');
  //   } else {
  //     sessionStorage.removeItem('isUnloading');
  //   }
  // }

  // @HostListener('window:unload')
  // unloadHandler() {
  //   if (sessionStorage.getItem('isUnloading')) {
  //     //this.forceLogout();
  //   }
  // }

  @HostListener('window:DOMContentLoaded')
  domContentLoadedHandler() {
    sessionStorage.removeItem('isUnloading');
  }

  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly authStore = inject(AuthStore);
  private readonly navigationService = inject(NavigationService);
  private readonly signalRService = inject(SignalRService);
  private readonly popupSession = inject(PopupSessionService);
  private readonly changelogUpdates = inject(ChangelogUpdatesService);
  private readonly cdkDialogService = inject(CdkDialogService);

  /** Tracks the currently-open ATR popup so a duplicate never gets stacked on top of it. */
  private popupDialogRef: DialogRef<unknown, unknown> | null = null;

  /** Subject that emits when the component is destroyed, used to complete subscriptions */
  private readonly destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    // TODO: There is an issue with loging in after a while, as verifyLastActivity() force logout. To verify
    //const needsLogout = !this.verifyLastActivity();
    const needsLogout = false;
    await this.authStore.refreshAuthInfo();
    if (this.authStore.isAuthenticated()) {
      const account = await this.authStore.requestAccount();
      void this.changelogUpdates.check();
      if (account?.identityId) {
        await this.initSignalRConnection(account.identityId);
      }
      await this.checkPendingPopups();
      if (this.isAuthenticating || window.location.pathname === '/') {
        this.navigationService.navigateToHomePage();
      }
    } else {
      await this.authStore.authorize();
      if (this.authStore.isAuthenticated()) {
        const account = await this.authStore.requestAccount();
        void this.changelogUpdates.check();
        if (account?.identityId) {
          await this.initSignalRConnection(account.identityId);
        }
        await this.checkPendingPopups();
        if (this.isAuthenticating || window.location.pathname === '/') {
          this.navigationService.navigateToHomePage();
        }
      } else {
        this.authStore.logoutLocal();
        await this.authStore.authorize();
      }
    }
  }

  /** Checks for any pending popups and displays them if necessary. */

  private async checkPendingPopups(): Promise<void> {
    if (this.popupDialogRef) {
      return;
    }

    const pending = await this.popupSession.checkPending();

    if (pending.atrPopupPending) {
      const result = await this.showPopup<AtrActivationPopupComponent, AtrActivationPopupResult>(
        AtrActivationPopupComponent
      );

      if (result === AtrActivationPopupResult.Understood) {
        await this.popupSession.acknowledge({
          atrPopupAcknowledged: true,
        });
      }
    }

    if (pending.bulkDeletePopupPending) {
      const result = await this.showPopup<BulkDeletePopupComponent, BulkDeletePopupResult>(
        BulkDeletePopupComponent
      );

      if (result === BulkDeletePopupResult.Understood) {
        await this.popupSession.acknowledge({
          bulkDeletePopupAcknowledged: true,
        });
      }
    }
  }

  private async showPopup<TComponent, TResult>(
    component: ComponentType<TComponent>
  ): Promise<TResult | undefined> {
    const dialogRef = this.cdkDialogService.open<TComponent, TResult>(component, {
      disableClose: true,
      width: POPUP_DEFAULTS.WIDTH,
      maxWidth: '90vw',
    });

    this.popupDialogRef = dialogRef as DialogRef<unknown, unknown>;

    try {
      return await new Promise<TResult | undefined>((resolve) => {
        dialogRef.closed.pipe(take(1)).subscribe((result) => {
          resolve(result);
        });
      });
    } finally {
      this.popupDialogRef = null;
    }
  }

  /**
   * Initializes the SignalR connection so Heorot (the admin/teacher dashboard) can
   * receive real-time settings notifications addressed to the logged-in user.
   *
   * Heorot has no central SettingsStore at the app level - settings are loaded on
   * demand by the per-app settings views. We do not auto-apply incoming settings
   * here; we just keep the connection alive so the active settings view can react
   * via its own subscription if needed, and we log notifications (non-production
   * only) so admins can confirm cross-instance delivery in the browser console.
   */
  private async initSignalRConnection(userIdentityId: string): Promise<void> {
    // Subscribe BEFORE starting the connection so a push that arrives during the
    // connect handshake can't slip through the gap (the subjects have no replay).
    this.signalRService.settingsUpdated$.pipe(takeUntil(this.destroy$)).subscribe((settings) => {
      if (!this.environment.production) console.log('[Heorot] Received settings update:', settings);
    });

    this.signalRService.reconnected$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.environment.production) console.log('[Heorot] SignalR reconnected');
    });

    try {
      await this.signalRService.startConnection(userIdentityId);
    } catch (error) {
      console.error('[Heorot] Failed to initialize SignalR connection:', error);
      // Non-critical failure - the dashboard works without real-time notifications
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.signalRService.stopConnection();
  }

  async forceLogout() {
    const duarationEndpoint = `${this.environment.baseApiUrl}/auth/token/revocation`;
    const body = JSON.stringify({
      spaClientId: this.environment.identityClientId,
      tokens: [
        { token: this.authStore.authInfo().accessToken, tokenTypeHint: 'access_token' },
        { token: this.authStore.authInfo().refreshToken, tokenTypeHint: 'refresh_token' },
      ],
    });
    try {
      const authInfo = this.authStore.authInfo();
      this.authStore.logoutLocal();
      fetch(duarationEndpoint, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authInfo.accessToken}`,
        },
        keepalive: true,
      });
    } catch (e) {
      console.error(`Failed to send revocation request: ${e}`);
    }
  }
}
